import EmptyDataRow from '@/components/DataTable/EmptyDataRow'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Plus, Trash, Loader2, Edit, Eye, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAppStore } from '@/store'
import Modal from '@/components/Modal'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Checkbox } from '@/components/ui/checkbox'
import type {
  OnboardingQuestion,
  OnboardingQuestionType,
  CreateOnboardingQuestionRequest,
  UpdateOnboardingQuestionRequest,
} from '@/service/api'
import { useTranslation } from '@/i18n'

function useQuestionTypeOptions() {
  const { t } = useTranslation()
  return [
    { value: 'text' as OnboardingQuestionType, label: t('common:onboarding_questions.question_types.text_input') },
    { value: 'number' as OnboardingQuestionType, label: t('common:onboarding_questions.question_types.number_input') },
    { value: 'single_select' as OnboardingQuestionType, label: t('common:onboarding_questions.question_types.single_select') },
    { value: 'multi_select' as OnboardingQuestionType, label: t('common:onboarding_questions.question_types.multi_select') },
  ]
}

function useFormatQuestionType() {
  const { t } = useTranslation()
  return (type: OnboardingQuestionType) => {
    const labels: Record<OnboardingQuestionType, string> = {
      text: t('common:onboarding_questions.question_types.text'),
      number: t('common:onboarding_questions.question_types.number'),
      single_select: t('common:onboarding_questions.question_types.single_select'),
      multi_select: t('common:onboarding_questions.question_types.multi_select'),
    }
    return labels[type] || type
  }
}

interface QuestionFormData {
  questionText: string
  questionType: OnboardingQuestionType
  options: string[]
  displayOrder: number
  isRequired: boolean
}

const defaultFormData: QuestionFormData = {
  questionText: '',
  questionType: 'text',
  options: [],
  displayOrder: 0,
  isRequired: false,
}

export default function OnboardingQuestions() {
  const { t } = useTranslation()
  const questionTypeOptions = useQuestionTypeOptions()
  const formatQuestionType = useFormatQuestionType()

  const {
    onboardingQuestions,
    isOnboardingQuestionsLoading,
    fetchOnboardingQuestionsAdmin,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    allUsersAnswers,
    fetchAllUsersAnswers,
    user,
  } = useAppStore()

  const isAdmin = user?.role === 'admin'

  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAnswersModal, setShowAnswersModal] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState<OnboardingQuestion | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [formData, setFormData] = useState<QuestionFormData>(defaultFormData)
  const [newOption, setNewOption] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Filter to show only tenant-specific questions (not global defaults) in admin view
  const tenantQuestions = onboardingQuestions.filter(q => q.tenant_id !== null)
  const defaultQuestions = onboardingQuestions.filter(q => q.is_default)

  const COLS = [
    t('common:onboarding_questions.table_headers.question'),
    t('common:onboarding_questions.table_headers.type'),
    t('common:onboarding_questions.table_headers.required'),
    t('common:onboarding_questions.table_headers.order'),
    t('common:onboarding_questions.table_headers.status'),
    ...(isAdmin ? [''] : [])
  ]

  useEffect(() => {
    if (isAdmin) {
      fetchOnboardingQuestionsAdmin()
    }
  }, [fetchOnboardingQuestionsAdmin, isAdmin])

  const handleCloseAddModal = () => {
    setShowAddModal(false)
    setFormData(defaultFormData)
    setNewOption('')
  }

  const handleCloseEditModal = () => {
    setShowEditModal(false)
    setEditingQuestion(null)
    setFormData(defaultFormData)
    setNewOption('')
  }

  const handleEdit = (question: OnboardingQuestion) => {
    setEditingQuestion(question)
    setFormData({
      questionText: question.question_text,
      questionType: question.question_type,
      options: question.options || [],
      displayOrder: question.display_order,
      isRequired: question.is_required,
    })
    setShowEditModal(true)
  }

  const handleAddOption = () => {
    if (!newOption.trim()) return
    if (formData.options.includes(newOption.trim())) {
      toast.error(t('common:onboarding_questions.form.option_exists'))
      return
    }
    setFormData({ ...formData, options: [...formData.options, newOption.trim()] })
    setNewOption('')
  }

  const handleRemoveOption = (index: number) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index),
    })
  }

  const validateForm = (): boolean => {
    if (!formData.questionText.trim()) {
      toast.error(t('common:onboarding_questions.form.question_required'))
      return false
    }
    if (['single_select', 'multi_select'].includes(formData.questionType) && formData.options.length < 2) {
      toast.error(t('common:onboarding_questions.form.options_required'))
      return false
    }
    return true
  }

  const handleSubmitCreate = async () => {
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      const request: CreateOnboardingQuestionRequest = {
        questionText: formData.questionText.trim(),
        questionType: formData.questionType,
        options: ['single_select', 'multi_select'].includes(formData.questionType) ? formData.options : undefined,
        displayOrder: formData.displayOrder,
        isRequired: formData.isRequired,
      }
      await createQuestion(request)
      handleCloseAddModal()
    } catch {
      // Error handled in store
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmitUpdate = async () => {
    if (!editingQuestion || !validateForm()) return

    setIsSubmitting(true)
    try {
      const request: UpdateOnboardingQuestionRequest = {
        questionText: formData.questionText.trim(),
        questionType: formData.questionType,
        options: ['single_select', 'multi_select'].includes(formData.questionType) ? formData.options : undefined,
        displayOrder: formData.displayOrder,
        isRequired: formData.isRequired,
      }
      await updateQuestion(editingQuestion.id, request)
      handleCloseEditModal()
    } catch {
      // Error handled in store
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirmId) return
    try {
      await deleteQuestion(deleteConfirmId)
    } finally {
      setDeleteConfirmId(null)
    }
  }

  const handleViewAnswers = () => {
    fetchAllUsersAnswers()
    setShowAnswersModal(true)
  }

  const getAnswerDisplay = (questionId: string, answers: { question_id: string; answer_value: string | number | string[] }[]) => {
    const answer = answers.find(a => a.question_id === questionId)
    if (!answer) return '-'
    const value = answer.answer_value
    if (Array.isArray(value)) return value.join(', ')
    return String(value)
  }

  if (!isAdmin) {
    return (
      <div className="border border-[#E4E7EC] dark:border-dark-border rounded-2xl shadow-[0,1px,2px,0,rgba(16,24,40,0.05)] px-6 py-5 bg-white dark:bg-dark-card">
        <div className="text-center py-8 text-[#667085] dark:text-dark-text-muted">
          {t('common:onboarding_questions.admin_only')}
        </div>
      </div>
    )
  }

  const questionFormContent = (
    <div className="grid gap-4 py-4">
      <div className="grid gap-2">
        <Label htmlFor="questionText">{t('common:onboarding_questions.form.question_label')}</Label>
        <Input
          id="questionText"
          value={formData.questionText}
          placeholder={t('common:onboarding_questions.form.question_placeholder')}
          onChange={(e) => setFormData({ ...formData, questionText: e.target.value })}
          required
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="questionType">{t('common:onboarding_questions.form.type_label')}</Label>
        <Select
          value={formData.questionType}
          onValueChange={(value: OnboardingQuestionType) =>
            setFormData({ ...formData, questionType: value, options: [] })
          }
        >
          <SelectTrigger className="w-full" id="questionType">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {questionTypeOptions.map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {['single_select', 'multi_select'].includes(formData.questionType) && (
        <div className="grid gap-2">
          <Label>{t('common:onboarding_questions.form.options_label')}</Label>
          <div className="flex gap-2">
            <Input
              value={newOption}
              placeholder={t('common:onboarding_questions.form.options_placeholder')}
              onChange={(e) => setNewOption(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOption())}
            />
            <Button type="button" size="sm" onClick={handleAddOption}>
              {t('common:onboarding_questions.buttons.add')}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.options.map((option, index) => (
              <div
                key={index}
                className="flex items-center gap-1 bg-gray-100 dark:bg-dark-accent px-2 py-1 rounded text-sm"
              >
                <span>{option}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveOption(index)}
                  className="text-gray-500 dark:text-dark-text-muted hover:text-red-500"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="displayOrder">{t('common:onboarding_questions.form.display_order')}</Label>
          <Input
            id="displayOrder"
            type="number"
            min="0"
            value={formData.displayOrder}
            onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
          />
        </div>
        <div className="flex items-center gap-2 pt-6">
          <Checkbox
            id="isRequired"
            checked={formData.isRequired}
            onCheckedChange={(checked) => setFormData({ ...formData, isRequired: !!checked })}
          />
          <Label htmlFor="isRequired" className="cursor-pointer">{t('common:onboarding_questions.form.required_label')}</Label>
        </div>
      </div>
    </div>
  )

  return (
    <>
      <div className="border border-[#E4E7EC] dark:border-dark-border rounded-2xl shadow-[0,1px,2px,0,rgba(16,24,40,0.05)] px-6 py-5 bg-white dark:bg-dark-card">
        <div className="pb-5 border-b border-[#E4E7EC] dark:border-dark-border">
          <div className="flex flex-col md:flex-row gap-4 items-start justify-between">
            <div>
              <div className="text-lg text-[#101828] dark:text-dark-text font-semibold mb-1">
                {t('common:onboarding_questions.title')}
              </div>
              <div className="text-sm text-[#475467] dark:text-gray-400">
                {t('common:onboarding_questions.description')}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleViewAnswers}>
                <Eye className="w-4 h-4" />
                <span className="text-sm font-semibold">{t('common:onboarding_questions.view_answers')}</span>
              </Button>
              <Button size="sm" onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4" />
                <span className="text-sm font-semibold">{t('common:onboarding_questions.add_question')}</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Default Questions Section */}
        {defaultQuestions.length > 0 && (
          <div className="mt-4 mb-4">
            <div className="text-sm font-medium text-[#667085] dark:text-dark-text-muted mb-2">{t('common:onboarding_questions.default_questions')}</div>
            <div className="bg-gray-50 dark:bg-dark-muted rounded-lg p-3 space-y-2">
              {defaultQuestions.map((q, idx) => (
                <div key={q.id} className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500 dark:text-dark-text-muted">{idx + 1}.</span>
                  <span>{q.question_text}</span>
                  <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-2 py-0.5 rounded">
                    {formatQuestionType(q.question_type)}
                  </span>
                  {q.is_required && (
                    <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 px-2 py-0.5 rounded">{t('common:onboarding_questions.form.required_label')}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Custom Questions Table */}
        <div className="mt-4">
          <div className="text-sm font-medium text-[#667085] dark:text-dark-text-muted mb-2">{t('common:onboarding_questions.custom_questions')}</div>
          <Table>
            <TableHeader className="bg-slate-50 dark:bg-dark-muted">
              <TableRow>
                {COLS.map((col) => (
                  <TableHead key={col} className={`${col === '' ? 'w-[100px]' : ''} py-3 px-6`}>
                    {col}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isOnboardingQuestionsLoading ? (
                <TableRow>
                  <TableCell colSpan={COLS.length} className="text-center py-8">
                    <div className="flex justify-center items-center">
                      <Loader2 className="w-6 h-6 animate-spin mr-2" />
                      <span>{t('common:onboarding_questions.loading_questions')}</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : tenantQuestions.length > 0 ? (
                tenantQuestions.map((question) => (
                  <TableRow key={question.id}>
                    <TableCell className="!p-6 max-w-[300px]">
                      <span className="font-medium truncate block">{question.question_text}</span>
                    </TableCell>
                    <TableCell className="!p-6">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                        {formatQuestionType(question.question_type)}
                      </span>
                    </TableCell>
                    <TableCell className="!p-6">
                      {question.is_required ? (
                        <span className="text-green-600">{t('common:onboarding_questions.required.yes')}</span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">{t('common:onboarding_questions.required.no')}</span>
                      )}
                    </TableCell>
                    <TableCell className="!p-6">{question.display_order}</TableCell>
                    <TableCell className="!p-6">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        question.is_active ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-gray-100 dark:bg-dark-accent text-gray-800 dark:text-dark-text'
                      }`}>
                        {question.is_active ? t('common:onboarding_questions.status.active') : t('common:onboarding_questions.status.inactive')}
                      </span>
                    </TableCell>
                    <TableCell className="!p-6 w-[100px]">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(question)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirmId(question.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <EmptyDataRow
                  message={t('common:onboarding_questions.no_custom_questions')}
                  colSpan={COLS.length}
                />
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add Question Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={handleCloseAddModal}
        title={t('common:onboarding_questions.add_modal.title')}
        description={t('common:onboarding_questions.add_modal.description')}
        className="w-[500px]"
        footer={
          <div className="flex gap-3 w-full">
            <Button variant="outline" onClick={handleCloseAddModal} disabled={isSubmitting} className="flex-1">
              {t('common:onboarding_questions.buttons.cancel')}
            </Button>
            <Button onClick={handleSubmitCreate} disabled={isSubmitting} className="flex-1">
              {isSubmitting ? t('common:onboarding_questions.buttons.creating') : t('common:onboarding_questions.buttons.create')}
            </Button>
          </div>
        }
      >
        {questionFormContent}
      </Modal>

      {/* Edit Question Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        title={t('common:onboarding_questions.edit_modal.title')}
        description={t('common:onboarding_questions.edit_modal.description')}
        className="w-[500px]"
        footer={
          <div className="flex gap-3 w-full">
            <Button variant="outline" onClick={handleCloseEditModal} disabled={isSubmitting} className="flex-1">
              {t('common:onboarding_questions.buttons.cancel')}
            </Button>
            <Button onClick={handleSubmitUpdate} disabled={isSubmitting} className="flex-1">
              {isSubmitting ? t('common:onboarding_questions.buttons.saving') : t('common:onboarding_questions.buttons.save')}
            </Button>
          </div>
        }
      >
        {questionFormContent}
      </Modal>

      {/* View Answers Modal */}
      <Modal
        isOpen={showAnswersModal}
        onClose={() => setShowAnswersModal(false)}
        title={t('common:onboarding_questions.answers_modal.title')}
        description={t('common:onboarding_questions.answers_modal.description')}
        className="w-[800px] max-h-[80vh]"
        footer={
          <Button onClick={() => setShowAnswersModal(false)} className="w-full">
            {t('common:onboarding_questions.buttons.close')}
          </Button>
        }
      >
        <div className="max-h-[400px] overflow-y-auto">
          {!allUsersAnswers ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>{t('common:onboarding_questions.answers_modal.loading')}</span>
            </div>
          ) : allUsersAnswers.users.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-dark-text-muted">
              {t('common:onboarding_questions.answers_modal.no_answers')}
            </div>
          ) : (
            <Table>
              <TableHeader className="bg-slate-50 dark:bg-dark-muted sticky top-0">
                <TableRow>
                  <TableHead className="py-3 px-4">{t('common:onboarding_questions.answers_modal.user')}</TableHead>
                  {onboardingQuestions.map((q) => (
                    <TableHead key={q.id} className="py-3 px-4 max-w-[150px]">
                      <span className="truncate block" title={q.question_text}>
                        {q.question_text.substring(0, 30)}...
                      </span>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {allUsersAnswers.users.map((userAnswers) => (
                  <TableRow key={userAnswers.user_id}>
                    <TableCell className="!p-4">
                      <div className="font-medium">{userAnswers.user_email || userAnswers.user_id}</div>
                      <div className="text-xs text-gray-500 dark:text-dark-text-muted">{userAnswers.submitted_at}</div>
                    </TableCell>
                    {onboardingQuestions.map((q) => (
                      <TableCell key={q.id} className="!p-4 max-w-[150px]">
                        <span className="truncate block">
                          {getAnswerDisplay(q.id, userAnswers.answers)}
                        </span>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common:onboarding_questions.delete_modal.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('common:onboarding_questions.delete_modal.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:onboarding_questions.buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {t('common:onboarding_questions.buttons.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
